import React from 'react';
import { useTranslation } from 'react-i18next';
import { translateDomain } from 'utils/labels_utils';
import styled from 'styled-components';

const Card = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  height: 100%;
  box-sizing: border-box;
`;

const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px 0;
`;
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';

const Subtitle = styled.p`
  font-family: 'Lato', sans-serif;
  margin: 0 0 16px 0;
  font-size: 12px;
  color: var(--vo-slate);
`;

const EmptyMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
  padding: 12px 0;
`;

const TooltipBox = styled.div`
  background-color: white;
  padding: 12px;
  border: 1px solid var(--vo-cloud);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
`;

const TooltipTitle = styled.p`
  margin: 0 0 4px 0;
  font-weight: 700;
  font-size: 14px;
`;

const TooltipValue = styled.p`
  margin: 0;
  font-size: 14px;
  color: var(--color-brand);
`;

const Footer = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: var(--vo-slate);
`;

export const DomainVulnerabilityRadar = ({ data }) => {
  const { t } = useTranslation(['dashboard', 'gap-analysis']);

  const isEmpty = !data ||
    (Array.isArray(data) && data.length === 0) ||
    (!Array.isArray(data) && (!data.domains || data.domains.length === 0));

  if (isEmpty) {
    return (
      <Card>
        <CardTitle>{t('dashboard:domainRadar.noDataTitle')}</CardTitle>
        <EmptyMsg>{t('dashboard:domainRadar.noData')}</EmptyMsg>
      </Card>
    );
  }

  let chartData;
  if (Array.isArray(data)) {
    chartData = data.map(domain => ({
      domain: translateDomain(t, domain.domain || domain.name),
      score: domain.percentage || domain.score,
      fullMark: 100
    }));
  } else {
    chartData = data.domains.map((domain, idx) => ({
      domain: translateDomain(t, domain),
      score: data.scores[idx] || 0,
      fullMark: 100
    }));
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <TooltipBox>
          <TooltipTitle>{data.payload.domain}</TooltipTitle>
          <TooltipValue>
            {t('dashboard:domainRadar.complianceRateValue', { value: data.value.toFixed(1) })}
          </TooltipValue>
        </TooltipBox>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardTitle>{t('dashboard:domainRadar.title')}</CardTitle>
      <Subtitle>{t('dashboard:domainRadar.subtitle')}</Subtitle>

      <ResponsiveContainer width="100%" height={400}>
        <RadarChart data={chartData}>
          <PolarGrid />
          <PolarAngleAxis dataKey="domain" />
          <PolarRadiusAxis angle={90} domain={[0, 100]} />
          <Radar
            name={t('dashboard:domainRadar.complianceRate')}
            dataKey="score"
            stroke={'var(--vo-capri)'}
            fill={'var(--vo-capri)'}
            fillOpacity={0.6}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
        </RadarChart>
      </ResponsiveContainer>

      <Footer>{t('dashboard:domainRadar.footer')}</Footer>
    </Card>
  );
};
